import { z } from "zod"

import { applicabilitySchema } from "./applicability"
import { propertyKeySchema } from "./property-key"

export const eligibilitySchema = z.enum([
  `ELIGIBLE`,
  `INELIGIBLE`,
  `COULD_BE_ELIGIBLE`,
])

export type IncentiveEligibility = z.infer<typeof eligibilitySchema>

export const incentiveTypeSchema = z.enum([
  `billCredit`,
  `discount`,
  `rebate`,
  `taxCredit`,
])

export type IncentiveType = z.infer<typeof incentiveTypeSchema>

// Define the schema for the Incentive object
export const incentiveSchema = z.object({
  /** Genability ID of the solar incentive */
  incentiveId: z.number(),
  /** "Master" ID for this incentive. Tracks a single
   * incentive across multiple versions. */
  masterIncentiveId: z.number(),
  /** One of ELIGIBLE, INELIGIBLE, or COULD_BE_ELIGIBLE.
   * If an incentive has any applicabilities that the user
   * has not supplied any values for and none of the
   * already-supplied applicabilities make the user INELIGIBLE,
   * eligibility will be set to COULD_BE_ELIGIBLE and requiredData
   * will be populated with the additional information that is required. */
  eligibility: z.string().refine((v) => eligibilitySchema.parse(v)),
  /** If an incentive has formula-based applicabilities
   * and the user does not provide all of the required
   * information for that applicability, eligibility will
   * be set to COULD_BE_ELIGIBLE and requiredData will
   * be populated with the additional information that is
   * required. */
  requiredData: z.array(applicabilitySchema).optional().nullable(),
  /** The name of the incentive */
  incentiveName: z.string(),
  /** The type of project that this incentive applies to. Always solarPv. */
  projectType: z.string(),
  /** The type of incentive. Can be a rebate, billCredit,
   * taxCredit, or discount */
  incentiveType: z.string().refine((v) => incentiveTypeSchema.parse(v)),
  /** If applicable, the state where this incentive is available. */
  state: z.string(),
  /** The Genability ID of the LSE by whom the incentive is managed. */
  lseId: z.number().nullable(),
  /** The name of the LSE providing the incentive. */
  lseName: z.string().nullable(),
  /** The short code for the LSE providing the incentive. */
  lseCode: z.string().nullable().optional(),
  /** The class of customer that is eligible for this
   * incentive. Our solar incentives are residential. */
  customerClass: z.string(),
  /** The date when the incentive became or will become effective. */
  startDate: z.string(),
  /** The date when the incentive expires. */
  endDate: z.string().nullable(),
  /** true or false, depending on whether there are incentive funds remaining */
  isExhausted: z.boolean(),
  /** The entity type that offers this incentive. In the
   * case of a tax credit, this is also used to determine
   *  if the incentive applies to state or federal taxes. */
  jurisdiction: z.string(),
  /** The quantity by which to multiply the incentive rate. */
  quantityKey: propertyKeySchema,
  /** The rate by which to multiply the applicable quantity. */
  rate: z.number(),
  /** The type of rate for this incentive. Can be COST_PER_UNIT or PERCENTAGE. */
  rateUnit: z.string(),
  /** The maximum quantity key (e.g. systemSizeAc) that
   * is eligible for this incentive. For example, if
   * quantityKey=systemSizeAc, the value of this field is
   * 50, and the system size is 60, then the first 50 kW
   * of the system is eligible for the incentive. */
  quantityKeyCap: z.string().nullable(),
  /** Maximum dollar amount (in US) of this incentive. */
  paymentCap: z.number().nullable().optional(),
  /** Quantity key that’s used to calculate the percent cost cap. */
  percentCostCapKey: propertyKeySchema.nullable().optional(),
  /** The maximum portion of the cost of the system cost
   * that can be covered by the incentive. */
  percentCostCap: z.number().nullable().optional(),
  /** The duration of the incentive in years. Will be
   * 1 if this is a one-time, up-front incentive. */
  paymentDuration: z.number(),
  /** The party to whom the incentive is paid. */
  incentivePaidTo: z.string().nullable(),
  /** Indicates if more than one incentive from this
   * LSE/jurisdiction can be used for a customer for
   * this projectType. True means only one incentive
   * from the LSE/jurisdiction can be used. False means
   * more than one incentive from the LSE/jurisdiction
   * can be used. */
  projectTypeExclusive: z.boolean().nullable().optional(),
  /** Free text field describing the incentive.
   * Designed to be used in a user interface as a
   * general overview. */
  summary: z.string(),
  /** Free text field describing the requirements
   * for the incentive. Designed to be used in a user
   * interface to give an overview of incentive’s requirements. */
  requirements: z.string().nullable(),
  /** A list of applicabilities. Applicabilities are
   *  used to determine whether the customer is eligible for this incentive. */
  applicabilities: z.array(applicabilitySchema).nullable(),
})

export type Incentive = z.infer<typeof incentiveSchema>
